@import '@styles/mixins.scss';
@import '@styles/colors.scss';
@import '@styles/typography.scss';
@import '@styles/fonts.scss';

/* -------------------------------------------------------------------------- */
/*  The endpaper — the SEO structure below the wall's end (plan step 6).      */
/*  Narcis tokens, Toile's HomeEndpaper shape. Same max-width container the   */
/*  legacy home's own .nWrap uses (1280px, 80/24/16px side padding).          */
/* -------------------------------------------------------------------------- */

.endpaper {
  display: flex;
  flex-direction: column;
  background-color: var(--bg);
}

.intro,
.catalog,
.faq {
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 48px 80px;

  @media (max-width: 900px) { padding: 48px 24px; }
  @media (max-width: 600px) { padding: 32px 16px; }
}

.intro {
  padding-top: 64px;
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.h1 {
  font-family: var(--font-display);
  font-style: italic;
  font-weight: 500;
  font-size: 34px;
  line-height: 1.15;
  color: var(--text);
  margin: 0;
}

.lede {
  font-size: 15px;
  line-height: 1.6;
  color: var(--text-muted);
  max-width: 640px;
  margin: 0;
}

/* The issue links inside the lede: the prose's own colour, underlined. */
.ledeLink {
  color: inherit;
  text-decoration: underline;
  text-decoration-color: var(--border-med);
  text-underline-offset: 0.2em;

  &:hover {
    color: var(--text);
    text-decoration-color: var(--text-muted);
  }
}

.stats {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-dim);
  margin: 8px 0 0;
}

.h2 {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 400;
  color: var(--text);
  margin: 0;
}

.catalog {
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.catalogHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.headerLinks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.headerLink {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 9999px;
  padding: 6px 16px;
  text-decoration: none;

  &:hover {
    border-color: var(--border-med);
  }
}

.catalogList {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.catalogLink {
  font-size: 13px;
  color: var(--text-muted);
  text-decoration: none;

  &:hover {
    color: var(--text);
    text-decoration: underline;
  }
}

.seeAll {
  align-self: flex-start;
  font-size: 13px;
  font-weight: 500;
  color: var(--yolk);
  text-decoration: none;

  &:hover {
    color: var(--yolk-hover);
  }
}

.faq {
  border-top: 1px solid var(--border);
}

/* The three SEO sections: a 2x2 grid across the full 1280px column, no side padding. */
.sections {
  max-width: 1280px;
  margin: 0 auto;
  padding: 48px 0;
  border-top: 1px solid var(--border);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sectionHeading {
  font-family: var(--font-display);
  font-style: italic;
  font-weight: 500;
  font-size: 20px;
  color: var(--text);
  margin: 0;
  padding-top: 0.286em;
  line-height: 1.2;
}

.sectionBody {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 400;
  color: var(--text-muted);
  max-width: 56ch;
  margin: 0;
  line-height: 1.6;
}

// An expansion target: stays empty unless the copy carries one.
.sectionExpand {
  border: 1px dashed var(--border-med);
  border-radius: var(--radius-sm);
  padding: 8px 12px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-dim);
  min-height: 0;

  &:empty { display: none; }
}

/* The FAQ accordion — no heading; the question reads at the h4 rhythm step. */
.faqList {
  display: flex;
  flex-direction: column;
}

.faqItem {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-top: 1px solid var(--border);
  padding: 1em 0;

  .iconOpen {
    display: none;
  }

  &[open] {
    .iconClosed {
      display: none;
    }

    .iconOpen {
      display: inline;
    }
  }

  &:last-child {
    border-bottom: 1px solid var(--border);
  }
}

.faqItemTitle {
  @include v-rhythm(0);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: color 150ms ease-in-out;
  cursor: pointer;
  user-select: none;
  @include font-body;

  &:hover {
    color: var(--text-muted);
  }

  &::-webkit-details-marker,
  &::marker {
    display: none;
    content: '';
  }
}

.faqQuestion {
  @include v-rhythm(1);
  display: block;
  font-weight: 300;
}

.faqItemAnswer {
  color: var(--text-muted);
}
